/**
 * Field-type registry (ADR 0009, docs/modules/custom-fields.md). Task S3.
 * Pure: the server, the portal and an offline kiosk all run this same code, so a rule exists once.
 * Messages are i18n keys (`error.field_*`), never English text.
 */
import { z } from 'zod';
// --- shared pieces ------------------------------------------------------------------------------
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_RE = /^(?=.*\d)[0-9 +()-]{3,32}$/;
/** True for a real calendar date written YYYY-MM-DD. */
export function isIsoDate(s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m)
        return false;
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
const isNil = (v) => v === undefined || v === null;
const blankString = (v) => isNil(v) || (typeof v === 'string' && v.trim() === '');
const OptionSchema = z.object({ id: z.string().regex(/^[a-z0-9]{1,40}$/, 'error.field_options_invalid'), label: z.string().trim().min(1).max(100) });
const OptionsConfig = z
    .object({
    options: z.array(OptionSchema).max(100).default([]),
    archivedOptions: z.array(OptionSchema).max(1000).default([]),
})
    .refine((c) => new Set([...c.options, ...c.archivedOptions].map((o) => o.id)).size === c.options.length + c.archivedOptions.length, {
    message: 'error.field_options_invalid',
});
const optionsProblem = (c) => (c.options.length === 0 ? 'error.field_options_required' : null);
const labelOf = (c, id) => [...c.options, ...c.archivedOptions].find((o) => o.id === id)?.label ?? '';
const activeIds = (c) => new Set(c.options.map((o) => o.id));
function findOption(c, text) {
    const t = text.trim().toLowerCase();
    const hit = c.options.find((o) => o.label.toLowerCase() === t || o.id === t);
    if (!hit)
        throw new Error('error.field_option');
    return hit.id;
}
function define(spec) {
    return spec;
}
const ALL = ['personal', 'sign_in', 'sign_out'];
const SIGN = ['sign_in', 'sign_out'];
const MODULE = 'custom-fields';
const hintsOf = (input, keyboard, extra = {}) => ({
    web: { input, ...(extra.autoComplete ? { autoComplete: extra.autoComplete } : {}) },
    kiosk: { keyboard, ...(extra.autoCapitalize ? { autoCapitalize: extra.autoCapitalize } : {}) },
});
// --- the twelve built-in types ------------------------------------------------------------------
const textSpec = define({
    key: 'text',
    module: MODULE,
    labelKey: 'fieldType.text',
    widget: 'text',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: true,
    configSchema: z.object({ maxLength: z.number().int().min(1).max(500).default(200) }),
    valueSchema: (c) => z.string().trim().max(c.maxLength, 'error.field_too_long'),
    isEmpty: blankString,
    toText: (v) => v,
    fromText: (t, c) => {
        const v = t.trim();
        if (v.length > c.maxLength)
            throw new Error('error.field_too_long');
        return v;
    },
    hints: hintsOf('text', 'default', { autoCapitalize: 'sentences' }),
    exportKind: 'text',
});
const longTextSpec = define({
    key: 'long_text',
    module: MODULE,
    labelKey: 'fieldType.long_text',
    widget: 'textarea',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: false,
    configSchema: z.object({ maxLength: z.number().int().min(1).max(5000).default(2000) }),
    valueSchema: (c) => z.string().trim().max(c.maxLength, 'error.field_too_long'),
    isEmpty: blankString,
    toText: (v) => v,
    fromText: (t, c) => {
        const v = t.trim();
        if (v.length > c.maxLength)
            throw new Error('error.field_too_long');
        return v;
    },
    hints: { web: { input: 'textarea', multiline: true }, kiosk: { keyboard: 'default', multiline: true, autoCapitalize: 'sentences' } },
    exportKind: 'text',
});
const numberSpec = define({
    key: 'number',
    module: MODULE,
    labelKey: 'fieldType.number',
    widget: 'number',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: true,
    configSchema: z
        .object({
        min: z.number().finite().optional(),
        max: z.number().finite().optional(),
        integer: z.boolean().default(false),
        unit: z.string().trim().max(20).default(''),
    })
        .refine((c) => c.min === undefined || c.max === undefined || c.min <= c.max, { message: 'error.field_range' }),
    valueSchema: (c) => z
        .number('error.field_number')
        .refine(Number.isFinite, { message: 'error.field_number' })
        .refine((n) => !c.integer || Number.isInteger(n), { message: 'error.field_whole_number' })
        .refine((n) => (c.min === undefined || n >= c.min) && (c.max === undefined || n <= c.max), { message: 'error.field_range' }),
    isEmpty: (v) => isNil(v) || v === '',
    toText: (v, c) => `${v}${c.unit ? ` ${c.unit}` : ''}`,
    fromText: (t, c) => {
        const n = Number(t.trim().replace(',', '.'));
        if (!Number.isFinite(n))
            throw new Error('error.field_number');
        if (c.integer && !Number.isInteger(n))
            throw new Error('error.field_whole_number');
        if ((c.min !== undefined && n < c.min) || (c.max !== undefined && n > c.max))
            throw new Error('error.field_range');
        return n;
    },
    hints: hintsOf('number', 'decimal-pad'),
    exportKind: 'number',
});
const emptyConfig = () => z.object({});
const emailSpec = define({
    key: 'email',
    module: MODULE,
    labelKey: 'fieldType.email',
    widget: 'email',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: true,
    configSchema: emptyConfig(),
    valueSchema: () => z.string().trim().toLowerCase().max(254, 'error.field_email').regex(EMAIL_RE, 'error.field_email'),
    isEmpty: blankString,
    toText: (v) => v,
    fromText: (t) => {
        const v = t.trim().toLowerCase();
        if (!EMAIL_RE.test(v) || v.length > 254)
            throw new Error('error.field_email');
        return v;
    },
    hints: hintsOf('email', 'email-address', { autoComplete: 'email', autoCapitalize: 'none' }),
    exportKind: 'text',
});
const phoneSpec = define({
    key: 'phone',
    module: MODULE,
    labelKey: 'fieldType.phone',
    widget: 'phone',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: true,
    configSchema: emptyConfig(),
    valueSchema: () => z.string().trim().regex(PHONE_RE, 'error.field_phone'),
    isEmpty: blankString,
    toText: (v) => v,
    fromText: (t) => {
        const v = t.trim();
        if (!PHONE_RE.test(v))
            throw new Error('error.field_phone');
        return v;
    },
    hints: hintsOf('tel', 'phone-pad', { autoComplete: 'tel' }),
    exportKind: 'text',
});
const dateSpec = define({
    key: 'date',
    module: MODULE,
    labelKey: 'fieldType.date',
    widget: 'date',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: true,
    configSchema: emptyConfig(),
    valueSchema: () => z.string().refine(isIsoDate, { message: 'error.field_date' }),
    isEmpty: blankString,
    toText: (v) => v,
    fromText: (t) => {
        const s = t.trim();
        const uk = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
        const v = uk ? `${uk[3]}-${uk[2].padStart(2, '0')}-${uk[1].padStart(2, '0')}` : s;
        if (!isIsoDate(v))
            throw new Error('error.field_date');
        return v;
    },
    hints: hintsOf('date', 'numeric'),
    exportKind: 'date',
});
const selectSpec = define({
    key: 'select',
    module: MODULE,
    labelKey: 'fieldType.select',
    widget: 'select',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: true,
    configSchema: OptionsConfig,
    valueSchema: (c) => z.string().refine((id) => activeIds(c).has(id), { message: 'error.field_option' }),
    isEmpty: blankString,
    toText: (v, c) => labelOf(c, v),
    fromText: (t, c) => findOption(c, t),
    configProblem: optionsProblem,
    hints: hintsOf('select', 'default'),
    exportKind: 'text',
});
const multiSelectSpec = define({
    key: 'multi_select',
    module: MODULE,
    labelKey: 'fieldType.multi_select',
    widget: 'multi_select',
    scopes: ALL,
    canBeSensitive: true,
    canBeStarred: false,
    configSchema: OptionsConfig,
    valueSchema: (c) => z
        .array(z.string(), 'error.field_option')
        .max(100)
        .refine((a) => new Set(a).size === a.length && a.every((id) => activeIds(c).has(id)), { message: 'error.field_option' }),
    isEmpty: (v) => isNil(v) || (Array.isArray(v) && v.length === 0),
    toText: (v, c) => v.map((id) => labelOf(c, id)).join(', '),
    fromText: (t, c) => [...new Set(t.split(/[;,]/).map((x) => x.trim()).filter(Boolean).map((x) => findOption(c, x)))],
    configProblem: optionsProblem,
    hints: hintsOf('multi_select', 'default'),
    exportKind: 'list',
});
const YES = new Set(['yes', 'y', 'true', '1']);
const NO = new Set(['no', 'n', 'false', '0']);
const yesNoSpec = define({
    key: 'yes_no',
    module: MODULE,
    labelKey: 'fieldType.yes_no',
    widget: 'yes_no',
    scopes: ALL,
    canBeSensitive: false,
    canBeStarred: true,
    configSchema: z.object({ requiredAnswer: z.boolean().nullable().default(null) }),
    valueSchema: (c) => z.boolean('error.field_invalid').refine((b) => c.requiredAnswer === null || b === c.requiredAnswer, { message: 'error.field_answer_not_allowed' }),
    isEmpty: isNil,
    toText: (v) => (v ? 'Yes' : 'No'),
    fromText: (t) => {
        const s = t.trim().toLowerCase();
        if (YES.has(s))
            return true;
        if (NO.has(s))
            return false;
        throw new Error('error.field_invalid');
    },
    hints: hintsOf('yes_no', 'default'),
    exportKind: 'boolean',
});
const signatureSpec = define({
    key: 'signature',
    module: MODULE,
    labelKey: 'fieldType.signature',
    widget: 'signature',
    scopes: SIGN,
    canBeSensitive: false,
    canBeStarred: false,
    configSchema: emptyConfig(),
    valueSchema: () => z.object({ fileId: z.uuid('error.field_invalid') }, 'error.field_invalid'),
    isEmpty: isNil,
    toText: () => 'Signed',
    hints: hintsOf('signature', 'default'),
    exportKind: 'text',
});
const DEFAULT_ACK = 'I have read and understood this.';
const acknowledgeSpec = define({
    key: 'acknowledge',
    module: MODULE,
    labelKey: 'fieldType.acknowledge',
    widget: 'acknowledge',
    scopes: SIGN,
    canBeSensitive: false,
    canBeStarred: false,
    configSchema: z.object({ message: z.string().trim().min(1).max(2000).default(DEFAULT_ACK) }),
    valueSchema: () => z.literal(true, 'error.field_must_acknowledge'),
    isEmpty: (v) => v !== true,
    toText: () => 'Yes',
    hints: hintsOf('acknowledge', 'default'),
    exportKind: 'boolean',
});
const hostSpec = define({
    key: 'host',
    module: MODULE,
    labelKey: 'fieldType.host',
    widget: 'member_picker',
    scopes: ['sign_in'],
    canBeSensitive: false,
    canBeStarred: true,
    configSchema: z.object({ groupIds: z.array(z.uuid()).max(200).default([]) }),
    valueSchema: () => z.object({ memberId: z.uuid('error.field_invalid'), name: z.string().trim().min(1, 'error.field_invalid').max(200) }, 'error.field_invalid'),
    isEmpty: isNil,
    toText: (v) => v.name,
    hints: hintsOf('member_picker', 'default'),
    exportKind: 'text',
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const BUILTIN_FIELD_TYPE_SPECS = [
    textSpec,
    longTextSpec,
    numberSpec,
    emailSpec,
    phoneSpec,
    dateSpec,
    selectSpec,
    multiSelectSpec,
    yesNoSpec,
    signatureSpec,
    acknowledgeSpec,
    hostSpec,
];
// --- lookups and checks -------------------------------------------------------------------------
/** The spec for a key: a built-in first, then the types a module added. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getFieldTypeSpec(key, extra = []) {
    return BUILTIN_FIELD_TYPE_SPECS.find((s) => s.key === key) ?? extra.find((s) => s.key === key);
}
const keyOf = (message, fallback = 'error.field_invalid') => (message && message.startsWith('error.') ? message : fallback);
/**
 * Checks one answer. An empty answer is `{ ok: true, value: null }` unless the field is required.
 * On success `value` is the normalised value to store (trimmed, e-mail lower-cased).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function validateFieldValue(spec, config, value, required) {
    const cfg = spec.configSchema.safeParse(config ?? {});
    if (!cfg.success)
        return { ok: false, messageKey: 'error.field_config_invalid' };
    if (spec.isEmpty(value)) {
        if (!required)
            return { ok: true, value: null };
        return { ok: false, messageKey: spec.widget === 'acknowledge' ? 'error.field_must_acknowledge' : 'error.field_required' };
    }
    const parsed = spec.valueSchema(cfg.data).safeParse(value);
    if (!parsed.success)
        return { ok: false, messageKey: keyOf(parsed.error.issues[0]?.message) };
    return { ok: true, value: parsed.data };
}
/** Parses and checks a field's config. Returns the cleaned config, or an i18n key. */
export function checkFieldConfig(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
spec, config) {
    const cfg = spec.configSchema.safeParse(config ?? {});
    if (!cfg.success)
        return { ok: false, messageKey: keyOf(cfg.error.issues[0]?.message, 'error.field_config_invalid') };
    const problem = spec.configProblem?.(cfg.data) ?? null;
    if (problem)
        return { ok: false, messageKey: problem };
    return { ok: true, config: cfg.data };
}
/** Plain text for lists, exports and badges. '' for an empty or unreadable value. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function fieldText(spec, config, value) {
    if (spec.isEmpty(value))
        return '';
    const cfg = spec.configSchema.safeParse(config ?? {});
    if (!cfg.success)
        return '';
    try {
        return spec.toText(value, cfg.data);
    }
    catch {
        return '';
    }
}
/** What GET /fields/types returns for one type. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function fieldTypeInfo(spec) {
    return {
        key: spec.key,
        module: spec.module,
        labelKey: spec.labelKey,
        widget: spec.widget,
        scopes: [...spec.scopes],
        canBeSensitive: spec.canBeSensitive,
        canBeStarred: spec.canBeStarred,
        configJsonSchema: z.toJSONSchema(spec.configSchema, { io: 'input', unrepresentable: 'any' }),
        ...(spec.hints ? { hints: spec.hints } : {}),
        ...(spec.exportKind ? { exportKind: spec.exportKind } : {}),
    };
}
//# sourceMappingURL=registry.js.map